import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { PageContainer } from "../components/PageContainer";
import { contactSchema, ContactFormData } from "../helpers/formValidation";
import { ErrorMessage } from "../components/form/ErrorMessage";
import {
  formBtnPrimaryClasses,
  formInputClasses,
  formLabelClasses,
  formTextAreaClasses,
} from "../styles/formClasses";
import {
  centeredPageHeaderClasses,
  pageHeadingClasses,
  pageLeadClasses,
  sectionCardClasses,
  sectionTitleClasses,
  subtleEyebrowClasses,
} from "../styles/pageClasses";
import { contactPageClasses } from "../styles/pageSpecificClasses";

export default function Contact() {
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: { name: "", email: "", message: "" },
  });

  const messageLength = watch("message").length;

  const onSubmit = async (_data: ContactFormData) => {
    setSubmitting(true);
    try {
      setSent(true);
      reset();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer maxWidth="7xl">
      <header className={centeredPageHeaderClasses}>
        <span className={subtleEyebrowClasses}>Contact</span>
        <h1 className={`mt-4 ${pageHeadingClasses}`}>Let&apos;s talk</h1>
        <p className={pageLeadClasses}>
          Questions, partnership ideas, or feedback on the platform are all welcome.
        </p>
      </header>

      <section className={`${contactPageClasses.section} ${sectionCardClasses}`}>
        <div>
          <h2 className={sectionTitleClasses}>Send us a message</h2>
          <p className={contactPageClasses.introCopy}>
            We usually respond within 1 to 2 business days.
          </p>
        </div>

        {sent && (
          <div className={contactPageClasses.successBanner}>
            Thanks. Your message has been sent.
          </div>
        )}

        <form onSubmit={handleSubmit(onSubmit)} className={contactPageClasses.form}>
          <div>
            <label htmlFor="name" className={formLabelClasses}>
              Name
            </label>
            <input
              {...register("name")}
              id="name"
              type="text"
              autoComplete="name"
              className={formInputClasses}
              placeholder="Your full name"
            />
            <ErrorMessage message={errors.name?.message} />
          </div>

          <div>
            <label htmlFor="email" className={formLabelClasses}>
              Email
            </label>
            <input
              {...register("email")}
              id="email"
              type="email"
              autoComplete="email"
              className={formInputClasses}
              placeholder="you@example.com"
            />
            <ErrorMessage message={errors.email?.message} />
          </div>

          <div>
            <label htmlFor="message" className={formLabelClasses}>
              Message
            </label>
            <textarea
              {...register("message")}
              id="message"
              rows={5}
              className={formTextAreaClasses}
              placeholder="How can we help?"
            />
            <ErrorMessage message={errors.message?.message} />
            <div className={contactPageClasses.counterRow}>
              <span>Be as detailed as you need.</span>
              <span>{messageLength}/1000</span>
            </div>
          </div>

          <div className={contactPageClasses.submitWrap}>
            <button type="submit" className={formBtnPrimaryClasses} disabled={submitting}>
              {submitting ? "Sending..." : "Send Message"}
            </button>
          </div>
        </form>

        <div className={contactPageClasses.footer}>
          Prefer email? Reach us at{" "}
          <a
            href="mailto:contact@summerjobsingreeceguide.com"
            className={contactPageClasses.emailLink}
          >
            contact@summerjobsingreeceguide.com
          </a>
        </div>
      </section>
    </PageContainer>
  );
}
